import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef, ElementType } from "react";

import { cn } from "helpers/cn";

const gridVariants = cva("grid", {
  variants: {
    // 24 is antd's basis, kept for the call sites whose spans don't reduce to a
    // smaller one. Prefer the smallest basis that expresses the spans: unlike
    // antd's gutter, which cost no width at all, a gap is taken out of the
    // container before the tracks are sized, so a grid's minimum width is
    // `(cols - 1) * gap`. Where 24 is unavoidable, keep the gap small or drop
    // the horizontal one with `gapX={0}` — at 24 columns `gap={4}` alone floors
    // the grid at 368px, wider than any phone.
    cols: {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
      5: "grid-cols-5",
      6: "grid-cols-6",
      10: "grid-cols-10",
      12: "grid-cols-12",
      24: "grid-cols-24"
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      baseline: "items-baseline",
      stretch: "items-stretch"
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
      around: "justify-around",
      evenly: "justify-evenly"
    },
    // Mirrors Flex's scale, plus the two larger steps a column gutter needs.
    gap: {
      0: "gap-0",
      0.5: "gap-0.5",
      1: "gap-1",
      1.5: "gap-1.5",
      2: "gap-2",
      3: "gap-3",
      4: "gap-4",
      5: "gap-5",
      6: "gap-6",
      8: "gap-8",
      12: "gap-12",
      16: "gap-16"
    },
    // antd gutters take a [horizontal, vertical] pair, so the two axes have to
    // be settable apart from each other.
    gapX: {
      0: "gap-x-0",
      0.5: "gap-x-0.5",
      1: "gap-x-1",
      1.5: "gap-x-1.5",
      2: "gap-x-2",
      3: "gap-x-3",
      4: "gap-x-4",
      5: "gap-x-5",
      6: "gap-x-6",
      8: "gap-x-8",
      12: "gap-x-12",
      16: "gap-x-16"
    },
    gapY: {
      0: "gap-y-0",
      0.5: "gap-y-0.5",
      1: "gap-y-1",
      1.5: "gap-y-1.5",
      2: "gap-y-2",
      3: "gap-y-3",
      4: "gap-y-4",
      5: "gap-y-5",
      6: "gap-y-6",
      8: "gap-y-8",
      12: "gap-y-12",
      16: "gap-y-16"
    },
    flow: {
      row: "grid-flow-row",
      col: "grid-flow-col",
      dense: "grid-flow-dense"
    },
    fullWidth: { true: "w-full" }
  },
  defaultVariants: { cols: 1 }
});

// Tailwind only emits classes it can find as literal strings, so each step of
// every span scale has to be spelled out here.
const gridItemVariants = cva("", {
  variants: {
    span: {
      1: "col-span-1",
      2: "col-span-2",
      3: "col-span-3",
      4: "col-span-4",
      5: "col-span-5",
      6: "col-span-6",
      7: "col-span-7",
      8: "col-span-8",
      9: "col-span-9",
      10: "col-span-10",
      11: "col-span-11",
      12: "col-span-12",
      13: "col-span-13",
      14: "col-span-14",
      15: "col-span-15",
      16: "col-span-16",
      17: "col-span-17",
      18: "col-span-18",
      19: "col-span-19",
      20: "col-span-20",
      21: "col-span-21",
      22: "col-span-22",
      23: "col-span-23",
      24: "col-span-24",
      full: "col-span-full"
    },
    // md is 768px, the same boundary as the app's mobile breakpoint and antd's
    // own `md`.
    spanMd: {
      1: "md:col-span-1",
      2: "md:col-span-2",
      3: "md:col-span-3",
      4: "md:col-span-4",
      5: "md:col-span-5",
      6: "md:col-span-6",
      7: "md:col-span-7",
      8: "md:col-span-8",
      9: "md:col-span-9",
      10: "md:col-span-10",
      11: "md:col-span-11",
      12: "md:col-span-12",
      13: "md:col-span-13",
      14: "md:col-span-14",
      15: "md:col-span-15",
      16: "md:col-span-16",
      17: "md:col-span-17",
      18: "md:col-span-18",
      19: "md:col-span-19",
      20: "md:col-span-20",
      21: "md:col-span-21",
      22: "md:col-span-22",
      23: "md:col-span-23",
      24: "md:col-span-24",
      full: "md:col-span-full"
    }
  }
});

type GridVariantProps = VariantProps<typeof gridVariants>;

type GridProps<T extends ElementType = "div"> = {
  as?: T;
} & GridVariantProps &
  Omit<ComponentPropsWithRef<T>, "as" | keyof GridVariantProps>;

const Grid = <T extends ElementType = "div">({
  className,
  as,
  cols,
  align,
  justify,
  gap,
  gapX,
  gapY,
  flow,
  fullWidth,
  ...props
}: GridProps<T>) => {
  const Comp = (as ?? "div") as ElementType;

  return (
    <Comp
      className={cn(
        gridVariants({ cols, align, justify, gap, gapX, gapY, flow, fullWidth }),
        className
      )}
      data-slot="grid"
      {...props}
    />
  );
};

type GridItemVariantProps = VariantProps<typeof gridItemVariants>;

// Call sites that pick a span from a config object rather than a literal need a
// name for the scale.
type GridSpan = GridItemVariantProps["span"];

type GridItemProps<T extends ElementType = "div"> = {
  as?: T;
} & GridItemVariantProps &
  Omit<ComponentPropsWithRef<T>, "as" | keyof GridItemVariantProps>;

const GridItem = <T extends ElementType = "div">({
  className,
  as,
  span,
  spanMd,
  ...props
}: GridItemProps<T>) => {
  const Comp = (as ?? "div") as ElementType;

  return (
    <Comp
      className={cn(gridItemVariants({ span, spanMd }), className)}
      data-slot="grid-item"
      {...props}
    />
  );
};

export { Grid, GridItem, gridVariants, gridItemVariants };
export type { GridProps, GridItemProps, GridSpan };
